'use client';

import { useCallback, useMemo } from 'react';

import CookieExpires from '../../components/cookie-expires';
import CookieService from '../../components/cookie-service';
import Table from '../../components/table';
import { useConsents } from '../../providers/consents';

interface Props {
  cookies: Array<any>;
  locale: string;
  placeholder: string;
  organization: string;
}

const CookiesTable = ({
  cookies,
  locale,
  placeholder,
  organization,
}: Props) => {
  const { selectedCookieType } = useConsents();

  const dimensions = useMemo(
    () => [
      { header: 'Name', axis: 'name', width: 15 },
      { header: 'Service', axis: 'service', width: 20 },
      { header: 'Expires', axis: 'expires', width: 10 },
      { header: 'Description', axis: 'body', width: 55 },
    ],
    []
  );

  const transform = useCallback(
    (cookie, axis) => {
      if (axis === 'service')
        return <CookieService cookie={cookie} organization={organization} />;
      if (axis === 'expires')
        return (
          <CookieExpires
            locale={locale}
            value={cookie[axis]}
            placeholder={placeholder}
          />
        );
      if (axis === 'body')
        return (
          <div className="pl-2 md:pl-0 text-right md:text-left">
            {cookie[axis]}
          </div>
        );
      return cookie[axis];
    },
    [locale, placeholder, organization]
  );

  const map = useCallback(
    (cookie) => ({
      data: dimensions.map((dimension) => ({
        ...dimension,
        value: transform(cookie, dimension.axis),
      })),
    }),
    [dimensions, transform]
  );

  const data = useMemo(
    () =>
      cookies
        .filter(({ typeHandle }) => typeHandle === selectedCookieType.handle)
        .map(map),
    [cookies, map, selectedCookieType]
  );

  return <Table dimensions={dimensions} data={data} />;
};

export default CookiesTable;
